import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { KeyRound, ArrowRight, X } from 'lucide-react';
import { readMfaGrace, clearMfaGrace } from '../../utils/mfaGrace';

/**
 * "Set up two-factor within N days" — shown after sign-in when the organisation requires 2FA and
 * this person has not enrolled yet. The sign-in response always carried the countdown (mfaGrace);
 * nothing displayed it, so the first anyone heard of the requirement was being refused at sign-in
 * once the window closed.
 *
 * Same shape as BillingNotice. Dismissing hides it for the rest of this browser session; the next
 * sign-in brings it back, because unlike a missing card it does eventually lock the person out.
 * Not shown on the page that fixes it (which shows the countdown itself).
 */
export default function MfaGraceNotice() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const grace = readMfaGrace();
  if (!grace || dismissed || pathname.startsWith('/account/security')) return null;

  const urgent = grace.daysLeft <= 3;
  const when = grace.daysLeft <= 0
    ? 'today'
    : `within ${grace.daysLeft} day${grace.daysLeft === 1 ? '' : 's'}`;

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 mb-4 ${urgent ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`} role="status">
      <KeyRound size={18} className={`shrink-0 ${urgent ? 'text-rose-600' : 'text-amber-600'}`} aria-hidden="true" />
      <div className="flex-1 min-w-[200px]">
        <p className={`text-sm font-semibold ${urgent ? 'text-rose-900' : 'text-amber-900'}`}>Set up two-factor authentication</p>
        <p className={`text-[13px] leading-snug ${urgent ? 'text-rose-800' : 'text-amber-800'}`}>
          Your organisation requires it. Set it up {when}, or you won’t be able to sign in after that.
        </p>
      </div>
      <Link to="/account/security" className="btn-primary btn-sm shrink-0">
        Set up now <ArrowRight size={14} aria-hidden="true" />
      </Link>
      <button
        type="button"
        onClick={() => { clearMfaGrace(); setDismissed(true); }}
        aria-label="Dismiss for now"
        title="Dismiss for now"
        className={`w-9 h-9 inline-flex items-center justify-center rounded-lg shrink-0 transition-colors duration-150 hover:bg-black/5 ${urgent ? 'text-rose-900' : 'text-amber-900'}`}
      >
        <X size={15} aria-hidden="true" />
      </button>
    </div>
  );
}
